import { useEffect, useState } from "react";

import { Check, Pencil, RefreshCw, X } from "lucide-react";

/** What to show for a property: the user's nickname when set, else Google's own name. */
const displayName = (property) =>
  property?.label?.trim() || property?.property_name || property?.property_id || "";

/**
 * Property switcher, grouped by the Google Analytics account each property belongs to.
 *
 * One Google account can span several GA accounts, and property names repeat across them
 * ("Website", "Main site"), so the account name is what disambiguates. Each property can also carry
 * a user-supplied nickname, since GA4's own names are often opaque ("GA4 - Website (12345)").
 */
const PropertySelector = ({
  properties = [],
  value,
  onChange,
  onRefresh,
  onRename,
  refreshing = false,
  disabled = false,
}) => {
  // A <select> onChange always yields a string, so compare as strings — otherwise a
  // numeric property_id from the API would never match and the rename control would vanish.
  const selected = properties.find((p) => String(p.property_id) === String(value)) ?? null;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  // Switching property mid-edit would otherwise save the draft onto the wrong one.
  useEffect(() => {
    setEditing(false);
    setDraft("");
  }, [value]);

  const accounts = properties.reduce((groups, property) => {
    const key = property.account_name || property.account_id;
    (groups[key] ||= []).push(property);
    return groups;
  }, {});

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await onRename(selected.property_id, draft);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  if (editing && selected) {
    return (
      <form onSubmit={save} className="flex items-center gap-2">
        <label htmlFor="ga-property-label" className="sr-only">
          Property nickname
        </label>
        <input
          id="ga-property-label"
          autoFocus
          value={draft}
          maxLength={80}
          placeholder={selected.property_name}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => event.key === "Escape" && setEditing(false)}
          className="bg-surface-container-lowest border border-primary rounded-xl px-3 py-2 text-sm text-on-surface w-64"
        />
        <button
          type="submit"
          disabled={saving}
          title="Save nickname"
          aria-label="Save nickname"
          className="p-2 rounded-xl bg-primary text-on-primary hover:opacity-90 transition-opacity disabled:opacity-60"
        >
          <Check className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => setEditing(false)}
          title="Cancel"
          aria-label="Cancel renaming"
          className="p-2 rounded-xl border border-surface-container-high bg-surface-container-low hover:bg-surface-container-high transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
        <span className="text-xs text-on-surface-variant">Blank clears the nickname</span>
      </form>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <label htmlFor="ga-property" className="sr-only">
        Google Analytics property
      </label>
      <select
        id="ga-property"
        value={value ?? ""}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled || properties.length === 0}
        className="bg-surface-container-lowest border border-outline-variant/50 rounded-xl px-3 py-2 text-sm text-on-surface max-w-[18rem] disabled:opacity-60"
      >
        {properties.length === 0 && <option value="">No properties found</option>}
        {Object.entries(accounts).map(([accountName, group]) => (
          <optgroup key={accountName} label={accountName}>
            {group.map((property) => (
              <option key={property.property_id} value={property.property_id}>
                {displayName(property)}
              </option>
            ))}
          </optgroup>
        ))}
      </select>

      {selected && (
        <button
          type="button"
          onClick={() => {
            setDraft(selected.label ?? "");
            setEditing(true);
          }}
          title="Rename this property"
          aria-label="Rename this property"
          className="p-2 rounded-xl border border-surface-container-high bg-surface-container-low hover:bg-surface-container-high transition-colors"
        >
          <Pencil className="w-4 h-4" />
        </button>
      )}

      <button
        type="button"
        onClick={onRefresh}
        disabled={refreshing}
        title="Refresh properties from Google"
        aria-label="Refresh properties from Google"
        className="p-2 rounded-xl border border-surface-container-high bg-surface-container-low hover:bg-surface-container-high transition-colors disabled:opacity-60"
      >
        <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
      </button>
    </div>
  );
};

export default PropertySelector;
